/**
 * Unica frontera del modulo con el backend. Ningun componente llama al API
 * directamente: si manana cambia un endpoint, se cambia aqui.
 */
import {
  consulta,
  modificarValorGenericaAlter,
  obtenerPermisosValorAlt,
  obtenerValores,
  obtenerValoresFiltros,
} from "../../../global/js/funciones";
import {
  API_BUSCAR_IDENTIDAD,
  API_BUSCAR_PERSONAS,
  API_GESTION,
  API_SOLICITUDES,
  CODIGO_PERIODO,
  ESTADO_ACTIVO,
  GENERICA_BLOQUES,
  GENERICA_SALAS,
  GENERICA_NIVELES_ARIA,
  MINIMO_BUSQUEDA,
} from "../constants";

const comoLista = (respuesta) => (Array.isArray(respuesta) ? respuesta : []);

const activos = (valores) =>
  comoLista(valores).filter(({ estado }) => `${estado}` === `${ESTADO_ACTIVO}`);

/**
 * Periodo academico vigente para biblioteca. Solo se muestra: al guardar, el
 * backend pone el suyo.
 *
 * @returns {Promise<string>} nombre del periodo, o "" si no hay uno activo.
 */
export const obtenerPeriodoVigente = async () => {
  const periodos = await obtenerValoresFiltros([
    { llave: "codigo", valor: CODIGO_PERIODO },
    { llave: "estado", valor: ESTADO_ACTIVO },
  ]);

  return periodos?.[0]?.nombre ?? "";
};

/**
 * Niveles de ARIA activos. La duracion, en minutos, se guarda en `valora`.
 *
 * @returns {Promise<Array<{ id, nombre, duracion }>>}
 */
export const obtenerNivelesAria = async () => {
  if (!GENERICA_NIVELES_ARIA) return [];

  // En la generica: nombre es el evento, descripcion de que trata, valora
  // los minutos, valorc el numero del nivel y valorb su nombre ("Exploratorio").
  const valores = await obtenerValores(GENERICA_NIVELES_ARIA);
  return activos(valores).map(({ id, nombre, valora, valorb, valorc, descripcion }) => ({
    id,
    nombre,
    duracion: Number(valora) || null,
    numeroNivel: Number(valorc) || null,
    nivel: valorb || "",
    descripcion: descripcion || "",
  }));
};

/** @returns {Promise<Array<{ id, nombre }>>} bloques activos. */
export const obtenerBloques = async () => {
  const valores = await obtenerValores(GENERICA_BLOQUES);
  return activos(valores).map(({ id, nombre }) => ({ id, nombre }));
};

/**
 * Salones de un bloque: los valores relacionados con el bloque en la tabla de
 * permisos (bloque -> salon), igual que en el AGIL viejo. Solo los de la
 * generica de salones: la tabla de permisos es comun a todo Emma y el bloque
 * podria estar relacionado con valores de otras genericas.
 *
 * @returns {Promise<Array<{ id, nombre }>>}
 */
export const obtenerSalones = async (idBloque) => {
  if (!idBloque) return [];

  const permisos = await obtenerPermisosValorAlt([
    { llave: "principal", valor: idBloque },
    { llave: "secundario__generica", valor: GENERICA_SALAS },
  ]);
  return comoLista(permisos).map(({ secundario }) => ({
    id: secundario.id,
    nombre: secundario.nombre,
  }));
};

// Estos dos endpoints ya existian en Emma: los consume tambien el AGIL viejo.
// Leen SICUC, asi que solo responden dentro de la red de la universidad, y el
// periodo lo resuelve el backend con la generica Per_agil.
const API_MATERIAS = "api/v1.0/integraciones/agil/profesor/materias";
const API_ESTUDIANTES_GRUPO = "api/v1.0/integraciones/agil/estudiante/vinculados";

/**
 * Responden 200 con { titulo, data } incluso cuando no hay resultados, asi que
 * la lista vacia no es un error.
 */
const consultarSicuc = (url, cuerpo) =>
  new Promise((resolve, reject) => {
    consulta(url, cuerpo, "post", (error, estado, resp) => {
      if (error || estado !== 200) {
        reject(error || new Error(resp?.titulo ?? "No fue posible consultar SICUC."));
        return;
      }
      resolve(Array.isArray(resp?.data) ? resp.data : []);
    });
  });

/**
 * Materias que dicta la persona en el periodo vigente.
 *
 * @returns {Promise<Array<{ id, nombre, cod_materia, materia, grupo, cod_grupo, nombre_programa }>>}
 *   `nombre` es lo que se ve en el select; el resto viaja en la solicitud.
 */
export const obtenerMaterias = async (identificacion) => {
  if (!identificacion) return [];

  const materias = await consultarSicuc(API_MATERIAS, { identificacion });
  return materias.map(({ id, cod_materia, materia, cod_grupo, grupo, nombre_programa }) => ({
    id,
    nombre: `${materia} / ${grupo}`,
    cod_materia,
    materia,
    grupo,
    cod_grupo,
    nombre_programa,
  }));
};

/**
 * Estudiantes matriculados en el grupo de una materia.
 *
 * @returns {Promise<Array<{ identificacion, nombre }>>}
 */
export const obtenerEstudiantesDeGrupo = async (codGrupo) => {
  if (!codGrupo) return [];

  const estudiantes = await consultarSicuc(API_ESTUDIANTES_GRUPO, { codigo: codGrupo });
  return estudiantes.map(({ identificacion, nombre_completo }) => ({
    identificacion,
    nombre: nombre_completo,
  }));
};

/**
 * Personas de Emma que coinciden con el texto: cedula, correo, usuario o
 * nombre. Es el mismo buscador que usan becas, bienestar y los demas
 * modulos, y como ellos no se filtra por el campo `es_estudiante`: en el
 * repositorio nadie lo usa, asi que no se puede confiar en que este bien
 * marcado. Quien busca distingue por la cedula que muestra cada opcion.
 *
 * @returns {Promise<Array>} vacia si el texto es corto; el servidor no
 *   busca con menos de MINIMO_BUSQUEDA caracteres.
 */
export const buscarEstudiantes = (texto) =>
  new Promise((resolve, reject) => {
    const dato = `${texto ?? ""}`.trim();
    if (dato.length < MINIMO_BUSQUEDA) {
      resolve([]);
      return;
    }

    consulta(API_BUSCAR_PERSONAS, { dato }, "post", (error, estado, resp) => {
      if (error || estado !== 200 || !Array.isArray(resp)) {
        reject(error || new Error("No fue posible buscar estudiantes."));
        return;
      }
      resolve(resp);
    });
  });

/**
 * Identidad de una persona por su numero de identificacion, para cuando no
 * esta registrada en Emma. La resuelve IDCUC, el servicio de identidades de
 * la universidad, al que Emma le pregunta por HTTP; responde tambien fuera
 * de la red de la universidad (verificado el 2026-09-18).
 *
 * Emma devuelve 200 con un objeto vacio tanto si la cedula no existe como si
 * el servicio fallo: los dos casos llegan aqui como `null`.
 *
 * @returns {Promise<?Object>} la persona con su identificacion, o null.
 */
export const buscarPorIdentificacion = (identificacion) =>
  new Promise((resolve, reject) => {
    const dato = `${identificacion ?? ""}`.trim();
    if (!dato) {
      resolve(null);
      return;
    }

    consulta(
      API_BUSCAR_IDENTIDAD,
      { identificacion: dato },
      "post",
      (error, estado, resp) => {
        if (error || estado !== 200) {
          reject(error || new Error("No fue posible consultar la identificacion."));
          return;
        }
        // La respuesta no repite la cedula consultada: se agrega aqui, que es
        // lo que la solicitud guarda junto con el nombre.
        resolve(
          resp?.primer_nombre ? { ...resp, identificacion: dato } : null,
        );
      },
    );
  });

/**
 * "tipo=Bib_Lib&periodo=20252", sin los filtros vacios. Una lista va
 * separada por comas: estado=Bib_Sol_E,Bib_Rev_E.
 */
const conFiltros = (url, filtros = {}) => {
  const query = Object.entries(filtros)
    .map(([llave, valor]) => [llave, Array.isArray(valor) ? valor.join(",") : valor])
    .filter(([, valor]) => valor)
    .map(([llave, valor]) => `${llave}=${encodeURIComponent(valor)}`)
    .join("&");
  return query ? `${url}?${query}` : url;
};

/**
 * GET que resuelve con la respuesta si `esValida` la acepta y, si no,
 * rechaza con el motivo que manda el backend en `titulo`.
 */
const consultarGet = (url, esValida, mensaje) =>
  new Promise((resolve, reject) => {
    consulta(url, null, null, (error, estado, resp) => {
      if (error || estado !== 200 || !esValida(resp)) {
        reject(error || new Error(resp?.titulo ?? mensaje));
        return;
      }
      resolve(resp);
    });
  });

/**
 * Una pagina de un listado paginado por el backend (PaginacionBiblioteca).
 * El backend cuenta las paginas desde 1; la pantalla, como TablePagination,
 * desde 0.
 *
 * @returns {Promise<{ total: number, solicitudes: Array }>}
 */
const consultarPagina = (url, filtros = {}, { pagina = 0, porPagina = 10 } = {}) =>
  consultarGet(
    conFiltros(url, { ...filtros, page: pagina + 1, page_size: porPagina }),
    (resp) => Array.isArray(resp?.results),
    "No fue posible cargar las solicitudes.",
  ).then(({ count, results }) => ({ total: count, solicitudes: results }));

/**
 * Solicitudes propias del usuario en sesion (GET api/v1.0/biblioteca/solicitudes).
 * Procesos es la bandeja de cada quien: ni el administrador ve las ajenas
 * aqui; esas estan en Gestion Biblioteca.
 *
 * Esta paginada en el servidor, como la de Gestion.
 *
 * @param {{ tipo?: string, estado?: string[], periodo?: string }} filtros por codigo.
 * @param {{ pagina: number, porPagina: number }} paginado `pagina` empieza en 0.
 * @returns {Promise<{ total: number, solicitudes: Array }>} rechaza si el
 *   servidor no responde con la pagina.
 */
export const listarSolicitudes = (filtros, paginado) =>
  consultarPagina(API_SOLICITUDES, filtros, paginado);

/**
 * Lo que necesitan los filtros de la tabla de Procesos del solicitante:
 * cuantas solicitudes propias hay en cada estado (del tipo, o de los dos) en
 * el periodo, y sus periodos mas el vigente.
 *
 * @returns {Promise<{ vigente, periodo, periodos, total, estados }>}
 */
export const obtenerMisFiltros = ({ tipo, periodo }) =>
  consultarGet(
    conFiltros(`${API_SOLICITUDES}/filtros`, { tipo, periodo }),
    (resp) => Array.isArray(resp?.estados),
    "No fue posible cargar los filtros.",
  );

/**
 * Una solicitud completa (GET api/v1.0/biblioteca/solicitudes/<id>): la
 * cabecera, los datos de su tipo, los estudiantes, los libros o los niveles y
 * el historial de estados.
 *
 * El backend responde 403 si la solicitud es de otra persona y 404 si no
 * existe; en los dos casos el motivo viene en `titulo`.
 *
 * @returns {Promise<Object>} rechaza con el motivo si no se puede mostrar.
 */
export const obtenerSolicitud = (id) =>
  consultarGet(
    `${API_SOLICITUDES}/${id}`,
    (resp) => Boolean(resp?.id),
    "No fue posible cargar la solicitud.",
  );

/**
 * Guarda los cambios de una solicitud (PUT api/v1.0/biblioteca/solicitudes/<id>).
 *
 * El servidor solo la deja editar a quien la radico y mientras siga
 * solicitada; si no, responde con el motivo.
 *
 * @returns {Promise<{ ok: boolean, titulo: string, estado: ?number, id: ?number }>}
 */
export const actualizarSolicitud = (id, payload) =>
  new Promise((resolve) => {
    consulta(`${API_SOLICITUDES}/${id}`, payload, "put", (error, estado, resp) => {
      if (error) {
        resolve({
          ok: false,
          estado: null,
          id: null,
          titulo: "No fue posible conectar con el servidor.",
        });
        return;
      }

      resolve({
        ok: estado === 200,
        estado,
        id: resp?.id ?? null,
        titulo: resp?.titulo ?? "Error al guardar los cambios.",
      });
    });
  });

/**
 * Crea una solicitud de biblioteca (POST api/v1.0/biblioteca/solicitudes).
 *
 * @param {Object} payload el que arma construirPayload (model/solicitud.js).
 * @returns {Promise<{ ok: boolean, titulo: string, estado: ?number, id: ?number }>}
 *   `estado` es el codigo HTTP: 200 guardada, 302 error de negocio (con el
 *   motivo en `titulo`), null si no hubo respuesta del servidor.
 */
export const guardarSolicitud = (payload) =>
  new Promise((resolve) => {
    consulta(API_SOLICITUDES, payload, "post", (error, estado, resp) => {
      if (error) {
        resolve({
          ok: false,
          estado: null,
          id: null,
          titulo: "No fue posible conectar con el servidor.",
        });
        return;
      }

      resolve({
        ok: estado === 200,
        estado,
        id: resp?.id ?? null,
        titulo: resp?.titulo ?? "Error al guardar la solicitud.",
      });
    });
  });

// --- Gestion Biblioteca ---------------------------------------------------
// Las solicitudes de todos. El backend responde 403 a quien no administra el
// modulo, con el motivo en `titulo`.

/**
 * Cuantas solicitudes de un tipo hay en cada estado del periodo.
 *
 * @param {string} tipo codigo del tipo de solicitud.
 * @param {string} [periodo] sin el, el backend usa el vigente.
 * @returns {Promise<{ periodo: string, total: number,
 *   estados: Array<{ codigo, nombre, solicitudes }> }>} todos los estados del
 *   tipo, tambien los que estan en cero, en el orden del proceso.
 */
/** Sin `tipo`, cuenta los dos tipos juntos. */
export const obtenerEstadosGestion = (tipo, periodo) =>
  consultarGet(
    conFiltros(`${API_GESTION}/estados`, { tipo, periodo }),
    (resp) => Array.isArray(resp?.estados),
    "No fue posible cargar los estados.",
  );

/**
 * Periodos que tienen solicitudes, mas el vigente.
 *
 * @returns {Promise<{ vigente: ?string, periodos: string[] }>}
 */
export const obtenerPeriodosGestion = () =>
  consultarGet(
    `${API_GESTION}/periodos`,
    (resp) => Array.isArray(resp?.periodos),
    "No fue posible cargar los periodos.",
  );

/**
 * Una pagina de las solicitudes de todos. Filtra y busca el servidor, para
 * no traer miles de filas al navegador.
 *
 * @param {{ tipo?, estado?, periodo?, buscar? }} filtros `buscar` es texto
 *   libre: nombre, identificacion o correo de quien la pidio, o la materia.
 * @param {{ pagina: number, porPagina: number }} paginado `pagina` empieza
 *   en 0, como la de TablePagination.
 * @returns {Promise<{ total: number, solicitudes: Array }>}
 */
export const listarSolicitudesGestion = (filtros, paginado) =>
  consultarPagina(`${API_GESTION}/solicitudes`, filtros, paginado);

/**
 * Todas las solicitudes que cumplen los filtros, sin paginar: las que van al
 * Excel.
 *
 * @returns {Promise<Array>}
 */
export const exportarSolicitudesGestion = (filtros) =>
  consultarGet(
    conFiltros(`${API_GESTION}/solicitudes`, { ...filtros, exportar: 1 }),
    Array.isArray,
    "No fue posible descargar las solicitudes.",
  );

/** Una solicitud completa, sea de quien sea. Misma forma que obtenerSolicitud. */
export const obtenerSolicitudGestion = (id) =>
  consultarGet(
    `${API_GESTION}/solicitudes/${id}`,
    (resp) => Boolean(resp?.id),
    "No fue posible cargar la solicitud.",
  );

/**
 * Cambia el periodo vigente del modulo: el nombre del valor Per_Bib, como
 * hace Gestion Practicas con Per_Pra.
 *
 * @returns {Promise<{ ok: boolean, titulo: string }>}
 */
export const actualizarPeriodoVigente = async (periodo) => {
  const [actual] = await obtenerValoresFiltros([
    { llave: "codigo", valor: CODIGO_PERIODO },
  ]);
  if (!actual) {
    return { ok: false, titulo: "No se encontro el periodo de biblioteca." };
  }

  const { error, estado, resp } = await modificarValorGenericaAlter(actual.id, {
    nombre: periodo,
  });
  if (error) return { ok: false, titulo: "No fue posible conectar con el servidor." };
  return {
    ok: estado === 200,
    titulo: resp?.titulo ?? "No fue posible actualizar el periodo.",
  };
};

/**
 * POST o PUT que nunca rechaza: resuelve con si se guardo, el mensaje del
 * backend y la respuesta completa (por ejemplo, el detalle actualizado).
 *
 * @returns {Promise<{ ok: boolean, titulo: string, datos: ?Object }>}
 */
const enviar = (url, payload, metodo, mensajeError) =>
  new Promise((resolve) => {
    consulta(url, payload, metodo, (error, estado, resp) => {
      if (error) {
        resolve({ ok: false, titulo: "No fue posible conectar con el servidor.", datos: null });
        return;
      }
      resolve({
        ok: estado === 200,
        titulo: resp?.titulo ?? mensajeError,
        datos: resp ?? null,
      });
    });
  });

/**
 * Lo que puede hacer en Gestion quien esta en sesion: si configura el modulo
 * (permisos y horarios) o solo gestiona.
 *
 * @returns {Promise<{ perfil: string, configura: boolean }>}
 */
export const obtenerPerfilGestion = () =>
  consultarGet(
    `${API_GESTION}/perfil`,
    (resp) => typeof resp?.configura === "boolean",
    "No fue posible cargar tu perfil de biblioteca.",
  );

/**
 * Pasa una solicitud al estado siguiente. El backend decide si se puede
 * (tipo, estado actual y permisos de quien gestiona) y exige el motivo al
 * rechazar.
 *
 * Al revisar un ARIA, `extra` lleva { recurso, bloque, salon }.
 *
 * @returns {Promise<{ ok, titulo, datos }>} `datos` es el detalle actualizado.
 */
export const cambiarEstadoSolicitud = (id, estado, mensaje, extra = {}) =>
  enviar(
    `${API_GESTION}/solicitudes/${id}/estado`,
    { ...extra, estado, mensaje },
    "post",
    "No fue posible gestionar la solicitud.",
  );

/** La cancela quien la radico, mientras siga abierta; `mensaje` es obligatorio. */
export const cancelarSolicitud = (id, mensaje) =>
  enviar(
    `${API_SOLICITUDES}/${id}/cancelar`,
    { mensaje },
    "post",
    "No fue posible cancelar la solicitud.",
  );

/**
 * Personas con perfil de administrador o auxiliar de biblioteca, con sus
 * permisos, y los tipos de solicitud con sus estados.
 *
 * @returns {Promise<{ tipos: Array<{ codigo, nombre, estados }>,
 *   gestores: Array<{ id, nombre, identificacion, correo, perfil, permisos }> }>}
 *   `permisos` es { "Bib_Lib": ["Bib_Sol_E", ...] }.
 */
export const obtenerGestores = () =>
  consultarGet(
    `${API_GESTION}/gestores`,
    (resp) => Array.isArray(resp?.gestores),
    "No fue posible cargar las personas de biblioteca.",
  );

/** Reemplaza los tipos y estados que gestiona una persona. */
export const guardarPermisosGestor = (id, permisos) =>
  enviar(
    `${API_GESTION}/gestores/${id}/permisos`,
    { permisos },
    "put",
    "No fue posible guardar los permisos.",
  );

// --- Auxiliares y horarios (fase 2) ----------------------------------------
// Cada endpoint responde con el estado completo actualizado, asi la pantalla
// se repinta con lo que dice el backend.

/**
 * Auxiliares de una solicitud: los asignados, los retirados (historial) y
 * los candidatos y en que acciones les cubre el horario.
 *
 * @returns {Promise<{ maneja_auxiliares, acciones, asignados, retirados, candidatos }>}
 */
export const obtenerAuxiliares = (id) =>
  consultarGet(
    `${API_GESTION}/solicitudes/${id}/auxiliares`,
    (resp) => Array.isArray(resp?.candidatos),
    "No fue posible cargar los auxiliares.",
  );

export const asignarAuxiliar = (id, auxiliar, accion) =>
  enviar(
    `${API_GESTION}/solicitudes/${id}/auxiliares`,
    { auxiliar, accion },
    "post",
    "No fue posible asignar el auxiliar.",
  );

export const retirarAuxiliar = (id, asignacion, nota) =>
  enviar(
    `${API_GESTION}/solicitudes/${id}/auxiliares/${asignacion}/retirar`,
    { nota },
    "post",
    "No fue posible retirar el auxiliar.",
  );

/** Recursos de ARIA. `requiere_sala`: hay que elegir bloque y salon. */
export const obtenerRecursos = () =>
  consultarGet(`${API_GESTION}/recursos`, Array.isArray, "No fue posible cargar los recursos.");

/**
 * Las causas por las que se rechaza una solicitud, de los dos tipos.
 *
 * @returns {Promise<Array<{ id, codigo, nombre, requiere_detalle }>>}
 *   `requiere_detalle`: al elegirla hay que escribir el detalle (Otro).
 */
export const obtenerCausasRechazo = () =>
  consultarGet(
    `${API_GESTION}/causas-rechazo`,
    Array.isArray,
    "No fue posible cargar las causas de rechazo.",
  );

// --- Horario de solicitudes (radicar y atencion) ------------------------------

/**
 * Los horarios de radicar y de atencion, para avisar antes de enviar. Cada
 * uno trae `texto` ("lunes a viernes de 07:00 a 17:00"), vacio si no se
 * configuro; y `puede_radicar` dice si en este momento se puede.
 *
 * @returns {Promise<{ radicar: { dias, texto }, atencion: { dias, texto }, puede_radicar: boolean }>}
 */
export const obtenerHorarioSolicitudes = () =>
  consultarGet(
    `${API_SOLICITUDES}/horario`,
    (resp) => Boolean(resp?.radicar && resp?.atencion),
    "No fue posible consultar el horario de biblioteca.",
  );

/** Lo mismo, para administrarlo (solo quien configura). */
export const obtenerHorarioSolicitudesGestion = () =>
  consultarGet(
    `${API_GESTION}/horario-solicitudes`,
    (resp) => Boolean(resp?.radicar && resp?.atencion),
    "No fue posible cargar el horario de solicitudes.",
  );

/**
 * Reemplaza un horario: `uso` es "radicar" o "atencion" y `dias` los
 * abiertos, [{ dia, abre, cierra }] con dia 0 = lunes. Sin dias, deja de
 * restringir.
 */
export const guardarHorarioSolicitudes = (uso, dias) =>
  enviar(
    `${API_GESTION}/horario-solicitudes`,
    { uso, dias },
    "put",
    "No fue posible guardar el horario.",
  );

/**
 * Horarios con sus auxiliares, y las personas de biblioteca que se les pueden
 * agregar.
 *
 * @returns {Promise<{ horarios: Array<{ id, hora_entrada, hora_salida, auxiliares }>, personas }>}
 *   Cada auxiliar trae `dias`, los que trabaja ese horario, como
 *   date.weekday() de Python: 0 es lunes (ver model/horarios.js).
 */
export const obtenerHorarios = () =>
  consultarGet(
    `${API_GESTION}/horarios`,
    (resp) => Array.isArray(resp?.horarios),
    "No fue posible cargar los horarios.",
  );

export const crearHorario = (horaEntrada, horaSalida) =>
  enviar(
    `${API_GESTION}/horarios`,
    { hora_entrada: horaEntrada, hora_salida: horaSalida },
    "post",
    "No fue posible crear el horario.",
  );

export const editarHorario = (id, horaEntrada, horaSalida) =>
  enviar(
    `${API_GESTION}/horarios/${id}`,
    { hora_entrada: horaEntrada, hora_salida: horaSalida },
    "put",
    "No fue posible editar el horario.",
  );

export const retirarHorario = (id) =>
  enviar(`${API_GESTION}/horarios/${id}`, null, "delete", "No fue posible retirar el horario.");

/**
 * Los dias en que una persona trabaja un horario: la agrega si no estaba, le
 * cambia los dias si ya estaba, y la quita si `dias` va vacio.
 */
export const asignarDiasEnHorario = (id, persona, dias) =>
  enviar(
    `${API_GESTION}/horarios/${id}/auxiliares/${persona}`,
    { dias },
    "put",
    "No fue posible guardar los días de la persona.",
  );


// --- Ejemplares (fase 3, Libro a tu clase) -----------------------------------

/**
 * Ejemplares de una solicitud: los activos con su estudiante, los retirados
 * (historial), los estudiantes a quienes se pueden asignar y los titulos que
 * pidio el docente.
 *
 * @returns {Promise<{ maneja_ejemplares, ejemplares, retirados, estudiantes, tematicas }>}
 */
export const obtenerEjemplares = (id) =>
  consultarGet(
    `${API_GESTION}/solicitudes/${id}/ejemplares`,
    (resp) => Array.isArray(resp?.ejemplares),
    "No fue posible cargar los ejemplares.",
  );

export const registrarEjemplar = (id, { codigoBarras, titulo, estudiante }) =>
  enviar(
    `${API_GESTION}/solicitudes/${id}/ejemplares`,
    { codigo_barras: codigoBarras, titulo, estudiante: estudiante || null },
    "post",
    "No fue posible registrar el ejemplar.",
  );

/**
 * Registra de una vez los ejemplares de la plantilla de Excel. Todo o nada:
 * si una fila tiene un error no se registra ninguno, y `titulo` dice cuales
 * corregir.
 *
 * @param {Array<{ fila, codigo_barras, titulo, estudiante }>} ejemplares
 */
export const cargarEjemplares = (id, ejemplares) =>
  enviar(
    `${API_GESTION}/solicitudes/${id}/ejemplares/cargar`,
    { ejemplares },
    "post",
    "No fue posible cargar los ejemplares.",
  );

export const asignarEjemplar = (id, ejemplar, estudiante) =>
  enviar(
    `${API_GESTION}/solicitudes/${id}/ejemplares/${ejemplar}/asignar`,
    { estudiante },
    "post",
    "No fue posible asignar el ejemplar.",
  );

/** `nota`, por que se retira, es obligatoria. */
export const retirarEjemplar = (id, ejemplar, nota) =>
  enviar(
    `${API_GESTION}/solicitudes/${id}/ejemplares/${ejemplar}/retirar`,
    { nota },
    "post",
    "No fue posible retirar el ejemplar.",
  );

// --- Calendario ----------------------------------------------------------------

/**
 * Solicitudes que el gestor puede ver cuya fecha cae en el rango del
 * calendario, y los estados y personas para los filtros.
 *
 * @param {{ desde: string, hasta: string, tipo?, estado?, auxiliar?, cerradas? }} filtros
 *   fechas en YYYY-MM-DD; `cerradas` en true incluye finalizadas, rechazadas y canceladas.
 * @returns {Promise<{ eventos, estados, auxiliares, yo }>}
 */
export const obtenerCalendario = ({ cerradas, ...filtros }) =>
  consultarGet(
    conFiltros(`${API_GESTION}/calendario`, { ...filtros, cerradas: cerradas ? 1 : "" }),
    (resp) => Array.isArray(resp?.eventos),
    "No fue posible cargar el calendario.",
  );

/**
 * Que estudiantes de la lista ya tienen cada nivel de ARIA en el periodo, y
 * si eso impide pedirlo (mas de la mitad). Al editar, `excluir` es la propia
 * solicitud, para que no se cuente a si misma. El backend lo vuelve a
 * comprobar al guardar.
 *
 * @returns {Promise<{ total, niveles: Array<{ id, vistos: Array<{ identificacion, nombre }>, bloquea }> }>}
 */
export const revisarNivelesVistos = ({ estudiantes, niveles, excluir }) =>
  enviar(
    `${API_SOLICITUDES}/niveles-vistos`,
    { estudiantes, niveles, excluir: excluir || null },
    "post",
    "No fue posible revisar los niveles.",
  ).then(({ ok, titulo, datos }) => {
    if (!ok) throw new Error(titulo);
    return datos;
  });

